/**
 * Export Renderer - Uses ReactDOMServer to render actual React components
 * This ensures export HTML matches Preview exactly
 *
 * Note: We avoid using @emotion/server because it requires Node.js polyfills
 * that CRA's webpack 5 doesn't provide. Instead, we use inline styles approach.
 */
import React from "react";
import ReactDOMServer from "react-dom/server";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { ThemeSettings } from "../types";
import { ContainerExport } from "../components/blocks/export/ContainerExport";
import { TextExport } from "../components/blocks/export/TextExport";
import { ButtonExport } from "../components/blocks/export/ButtonExport";
import { ImageExport } from "../components/blocks/export/ImageExport";
import { TestimonialExport } from "../components/blocks/export/TestimonialExport";
import { BeforeAfterExport } from "../components/blocks/export/BeforeAfterExport";
import { HeroExport } from "../components/blocks/export/HeroExport";
import { RootContainerExport } from "../components/blocks/export/RootContainerExport";
import { DividerExport } from "../components/blocks/export/DividerExport";
import { FAQExport } from "../components/blocks/export/FAQExport";
import { FooterExport } from "../components/blocks/export/FooterExport";
import { NavbarExport } from "../components/blocks/export/NavbarExport";
import { GalleryExport } from "../components/blocks/export/GalleryExport";
import { GoogleMapExport } from "../components/blocks/export/GoogleMapExport";
import { VideoExport } from "../components/blocks/export/VideoExport";
import { ServiceCardExport } from "../components/blocks/export/ServiceCardExport";
import { ServicesExport } from "../components/blocks/export/ServicesExport";
import { DoctorsExport } from "../components/blocks/export/DoctorsExport";
import { DoctorCardExport } from "../components/blocks/export/DoctorCardExport";
import { ContactInfoExport } from "../components/blocks/export/ContactInfoExport";
import { ContactFormExport } from "../components/blocks/export/ContactFormExport";
import { SocialLinksExport } from "../components/blocks/export/SocialLinksExport";
import { FloatingCTAExport } from "../components/blocks/export/FloatingCTAExport";
import { HeroSliderExport } from "../components/blocks/export/HeroSliderExport";
import { PromotionBannerExport } from "../components/blocks/export/PromotionBannerExport";
// Flexible Navbar
import { FlexNavbarExport } from "../components/blocks/export/FlexNavbarExport";
import { NavLogoExport } from "../components/blocks/export/NavLogoExport";
import { NavMenuExport } from "../components/blocks/export/NavMenuExport";
import { NavCTAExport } from "../components/blocks/export/NavCTAExport";
// Additional blocks
import { FeatureCardExport } from "../components/blocks/export/FeatureCardExport";
import { FilterableFeaturesExport } from "../components/blocks/export/FilterableFeaturesExport";
import { PricingCardExport } from "../components/blocks/export/PricingCardExport";
import { LineLoginButtonExport } from "../components/blocks/export/LineLoginButtonExport";
import { NavbarContainerExport } from "../components/blocks/export/NavbarContainerExport";
import { getGoogleFontsUrl, EXTERNAL_URLS } from "../config/apiConfig";

// Types for CraftJS content
interface CraftNode {
  type: { resolvedName: string };
  props: Record<string, unknown>;
  nodes?: string[];
  linkedNodes?: Record<string, string>;
  displayName?: string;
  custom?: Record<string, unknown>;
  isCanvas?: boolean;
}

interface CraftContent {
  ROOT: CraftNode;
  [key: string]: CraftNode;
}

// Map of component names to their export versions
const exportComponentMap: Record<string, React.ComponentType<any>> = {
  RootContainer: RootContainerExport,
  Container: ContainerExport,
  Text: TextExport,
  Button: ButtonExport,
  Image: ImageExport,
  Testimonial: TestimonialExport,
  BeforeAfter: BeforeAfterExport,
  Hero: HeroExport,
  Divider: DividerExport,
  FAQ: FAQExport,
  Footer: FooterExport,
  Navbar: NavbarExport,
  Gallery: GalleryExport,
  GoogleMap: GoogleMapExport,
  Video: VideoExport,
  ServiceCard: ServiceCardExport,
  Services: ServicesExport,
  Doctors: DoctorsExport,
  DoctorCard: DoctorCardExport,
  ContactInfo: ContactInfoExport,
  ContactForm: ContactFormExport,
  SocialLinks: SocialLinksExport,
  FloatingCTA: FloatingCTAExport,
  HeroSlider: HeroSliderExport,
  PromotionBanner: PromotionBannerExport,
  // Flexible Navbar
  FlexNavbar: FlexNavbarExport,
  NavLogo: NavLogoExport,
  NavMenu: NavMenuExport,
  NavCTA: NavCTAExport,
  // Additional blocks
  FeatureCard: FeatureCardExport,
  FilterableFeatures: FilterableFeaturesExport,
  PricingCard: PricingCardExport,
  LineLoginButton: LineLoginButtonExport,
  NavbarContainer: NavbarContainerExport,
};

const sectionMap: Record<string, { element: string; type: string }> = {
  Navbar:          { element: "navbar",        type: "menu"    },
  FlexNavbar:      { element: "navbar",        type: "menu"    },
  Hero:            { element: "hero",          type: "content" },
  HeroSlider:      { element: "hero",          type: "content" },
  PromotionBanner: { element: "promotion",     type: "content" },
  Services:        { element: "services",      type: "content" },
  Doctors:         { element: "doctors",       type: "content" },
  Testimonial:     { element: "testimonial",   type: "content" },
  FAQ:             { element: "faq",           type: "content" },
  Gallery:         { element: "gallery",       type: "image"   },
  GoogleMap:       { element: "map",           type: "map"     },
  ContactInfo:     { element: "contact",       type: "content" },
  ContactForm:     { element: "contact-form",  type: "content" },
  SocialLinks:     { element: "social",        type: "menu"    },
  Footer:          { element: "footer",        type: "footer"  },
  BeforeAfter:     { element: "before-after",  type: "image"   },
  FloatingCTA:     { element: "floating-cta",  type: "menu"    },
  Video:           { element: "video",         type: "video"   },
  Image:           { element: "image",         type: "image"   },
  FeatureCard:     { element: "feature-item",  type: "content" },
  FilterableFeatures: { element: "features",  type: "content" },
  PricingCard:     { element: "pricing-item",  type: "content" },
  LineLoginButton: { element: "line-login",    type: "action"  },
  NavbarContainer: { element: "navbar",        type: "menu"    },
};

/**
 * Create a component tree from CraftJS JSON content
 */
// Maps a linked-node key to the export-component props that receive the linked
// Text node's own color / fontFamily. Keeps published output in sync with the
// editor for per-element recoloring (e.g. a pink logo, a recolored footer name).
const linkedStyleTargetMap: Record<string, { color?: string; font?: string }> = {
  "navbar-logo-text": { color: "logoColor", font: "logoFontFamily" },
  "hero-title": { color: "titleColor" },
  "hero-subtitle": { color: "subtitleColor" },
  "promo-title": { color: "titleColor" },
  "promo-subtitle": { color: "subtitleColor" },
  "footer-name": { color: "clinicNameColor" },
  "footer-desc": { color: "descriptionColor" },
  "footer-address": { color: "addressColor" },
  "footer-phone": { color: "phoneColor" },
  "footer-email": { color: "emailColor" },
  "footer-copyright": { color: "copyrightColor" },
};

const createComponentTree = (nodeId: string, nodes: CraftContent, isRootChild = false): React.ReactNode => {
  const node = nodes[nodeId];
  if (!node) return null;

  const typeName = node.type?.resolvedName;
  if (!typeName) return null;

  // Get the export component or use a generic Box wrapper
  const Component = exportComponentMap[typeName];

  // Components with isCanvas + canMoveIn:false use <Element> linked nodes for internal text.
  // Their node.nodes contain these internal elements, NOT real children — skip them.
  const linkedNodeComponents = new Set(["Hero", "Navbar", "Footer", "ContactInfo", "ServiceCard", "DoctorCard", "FeatureCard", "PricingCard", "Testimonial", "PromotionBanner"]);
  const skipChildren = linkedNodeComponents.has(typeName);

  const isCurrentNodeRoot = nodeId === "ROOT";
  const childNodes = skipChildren ? [] : (node.nodes || []);
  const allChildren = childNodes
    .map((childId, index) => (
      <React.Fragment key={`${childId}-${index}`}>{createComponentTree(childId, nodes, isCurrentNodeRoot)}</React.Fragment>
    ))
    .filter(Boolean);

  // Sync linked Text/Button node text back to parent props (without mutating original)
  const syncedProps = { ...(node.props || {}) };
  const linkedNodes = node.linkedNodes || {};
  for (const [key, linkedId] of Object.entries(linkedNodes)) {
    const linkedNode = nodes[linkedId];
    if (!linkedNode) continue;
    const linkedType = linkedNode.type?.resolvedName;
    if ((linkedType === "Text" || linkedType === "Button") && linkedNode.props?.text !== undefined) {
      if (key.startsWith("nav-item-") && Array.isArray(syncedProps.navItems)) {
        const idx = parseInt(key.replace("nav-item-", ""), 10);
        if (!isNaN(idx) && (syncedProps.navItems as Array<Record<string, unknown>>)[idx]) {
          syncedProps.navItems = [...(syncedProps.navItems as Array<Record<string, unknown>>)];
          (syncedProps.navItems as Array<Record<string, unknown>>)[idx] = {
            ...(syncedProps.navItems as Array<Record<string, unknown>>)[idx],
            label: linkedNode.props.text,
          };
        }
      }
      if (key === "footer-name") syncedProps.clinicName = linkedNode.props.text;
      if (key === "footer-desc") syncedProps.description = linkedNode.props.text;
      if (key === "footer-address") syncedProps.address = linkedNode.props.text;
      if (key === "footer-phone") syncedProps.phone = linkedNode.props.text;
      if (key === "footer-email") syncedProps.email = linkedNode.props.text;
      if (key === "footer-copyright") syncedProps.copyright = linkedNode.props.text;
      if (key === "hero-title") syncedProps.title = linkedNode.props.text;
      if (key === "hero-subtitle") syncedProps.subtitle = linkedNode.props.text;
      if (key === "hero-button") {
        syncedProps.buttonText = linkedNode.props.text;
        if (typeof linkedNode.props.link === "string") syncedProps.buttonLink = linkedNode.props.link;
        if (typeof linkedNode.props.target === "string") syncedProps.buttonTarget = linkedNode.props.target;
        if (typeof linkedNode.props.linkType === "string") syncedProps.buttonLinkType = linkedNode.props.linkType;
      }
      if (key === "contact-name") syncedProps.clinicName = linkedNode.props.text;
      // Navbar
      if (key === "navbar-logo-text") syncedProps.logoText = linkedNode.props.text;
      if (key === "navbar-cta") {
        syncedProps.ctaText = linkedNode.props.text;
        if (typeof linkedNode.props.link === "string") syncedProps.ctaLink = linkedNode.props.link;
        if (typeof linkedNode.props.target === "string") syncedProps.ctaTarget = linkedNode.props.target;
        if (typeof linkedNode.props.linkType === "string") syncedProps.ctaLinkType = linkedNode.props.linkType;
      }
      // PromotionBanner
      if (key === "promo-title") syncedProps.title = linkedNode.props.text;
      if (key === "promo-subtitle") syncedProps.subtitle = linkedNode.props.text;
      // PricingCard table headers
      if (key === "pricing-header-service") syncedProps.serviceLabel = linkedNode.props.text;
      if (key === "pricing-header-credit") syncedProps.serviceCreditLabel = linkedNode.props.text;
      if (key === "pricing-header-baht") syncedProps.bahtLabel = linkedNode.props.text;
      // PricingCard
      if (key === "pricing-plan") syncedProps.planName = linkedNode.props.text;
      if (key === "pricing-credit") syncedProps.credit = linkedNode.props.text;
      if (key === "pricing-price") syncedProps.price = linkedNode.props.text;
      if (key === "pricing-unit") syncedProps.unit = linkedNode.props.text;
      if (key === "pricing-note") syncedProps.note = linkedNode.props.text;
      if (key === "pricing-credit-label") syncedProps.creditLabel = linkedNode.props.text;
      if (key === "pricing-price-label") syncedProps.priceLabel = linkedNode.props.text;
      // ServiceCard / DoctorCard / FeatureCard / Testimonial
      if (key === "card-title") syncedProps.title = linkedNode.props.text;
      if (key === "card-desc") syncedProps.description = linkedNode.props.text;
      if (key === "doctor-name") syncedProps.name = linkedNode.props.text;
      if (key === "doctor-specialty") syncedProps.specialty = linkedNode.props.text;
      if (key === "doctor-desc") syncedProps.description = linkedNode.props.text;
      if (key === "testimonial-quote") syncedProps.quote = linkedNode.props.text;
      if (key === "testimonial-author") syncedProps.authorName = linkedNode.props.text;
      if (key === "testimonial-title") syncedProps.authorTitle = linkedNode.props.text;

      // Propagate per-element color / fontFamily set on the linked Text node so the
      // exported output matches what the editor renders (the editor renders these same
      // linked nodes). Only overrides when the export component exposes a companion prop;
      // the component falls back to its base color when the prop is absent.
      const styleTarget = linkedStyleTargetMap[key];
      if (styleTarget) {
        if (styleTarget.color && typeof linkedNode.props?.color === "string") {
          syncedProps[styleTarget.color] = linkedNode.props.color;
        }
        if (styleTarget.font && typeof linkedNode.props?.fontFamily === "string") {
          syncedProps[styleTarget.font] = linkedNode.props.fontFamily;
        }
      }
    }
  }

  // Resolve multilingual text props — keep objects for components that render data-i18n-* attrs
  const keepMultilingual = typeName === "Text" || typeName === "Button" || typeName === "Navbar";
  const componentProps: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(syncedProps)) {
    if (!keepMultilingual && typeof value === "object" && value !== null && !Array.isArray(value)) {
      const keys = Object.keys(value as Record<string, unknown>);
      const isMultilingual = keys.length > 0 && keys.length <= 3 && keys.every((k) => ["th", "en", "zh"].includes(k))
        && Object.values(value as Record<string, unknown>).every((v) => typeof v === "string");
      if (isMultilingual) {
        const obj = value as Record<string, string>;
        componentProps[key] = obj.th || obj.en || "";
        continue;
      }
    }
    componentProps[key] = value;
  }

  // If we have an export component, use it
  if (Component) {
    const element =
      allChildren.length > 0 ? (
        <Component {...componentProps}>{allChildren}</Component>
      ) : (
        <Component {...componentProps} />
      );

    // Only wrap with data-element for direct ROOT children (top-level sections)
    if (isRootChild) {
      const sectionInfo = sectionMap[typeName];
      if (sectionInfo) {
        return <div data-element={sectionInfo.element} data-type={sectionInfo.type}>{element}</div>;
      }
      return <div data-element="content" data-type="content">{element}</div>;
    }
    return element;
  }

  // Fallback: render as div with children (for components not yet converted)
  console.warn(`No export component for: ${typeName}, using fallback`);
  return <div style={{ width: "100%" }}>{allChildren}</div>;
};

/**
 * Create MUI theme from ThemeSettings
 */
const createMuiTheme = (theme: ThemeSettings) => {
  return createTheme({
    palette: {
      primary: {
        main: theme.primary,
      },
      secondary: {
        main: theme.secondary,
      },
    },
    typography: {
      fontFamily: `'${theme.font}', sans-serif`,
    },
  });
};

/**
 * Render CraftJS content to static HTML
 * Note: MUI sx prop styles are converted to inline styles by ReactDOMServer
 */
export const renderToStaticHtml = (content: string, theme: ThemeSettings): { html: string; css: string } => {
  let parsedContent: CraftContent;

  try {
    parsedContent = JSON.parse(content);
  } catch (error) {
    console.error("Error parsing content:", error);
    return { html: "<p>Error loading content</p>", css: "" };
  }

  // Create MUI theme
  const muiTheme = createMuiTheme(theme);

  // Create component tree
  const componentTree = createComponentTree("ROOT", parsedContent);

  // Render to string
  const appHtml = ReactDOMServer.renderToStaticMarkup(
    <ThemeProvider theme={muiTheme}>
      <div
        className="export-root"
        style={{
          fontFamily: `'${theme.font}', sans-serif`,
        }}
      >
        {componentTree}
      </div>
    </ThemeProvider>
  );

  // Additional CSS for MUI components that need class-based styles
  const additionalCss = `
    /* MUI CssBaseline styles */
    html {
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
      scroll-behavior: smooth;
    }
    *, *::before, *::after {
      box-sizing: inherit;
    }
    body {
      margin: 0;
    }
  `;

  return { html: appHtml, css: additionalCss };
};

/**
 * Get interactive JavaScript for Slider and FAQ
 */
const getInteractiveJS = (): string => {
  return `
// Mobile Menu Toggle
function toggleMobileMenu() {
  var drawer = document.querySelector('.mobile-drawer');
  var overlay = document.querySelector('.mobile-overlay');
  if (drawer && overlay) {
    drawer.classList.toggle('open');
    overlay.classList.toggle('open');
    document.body.style.overflow = drawer.classList.contains('open') ? 'hidden' : '';
  }
}

function closeMobileMenu() {
  var drawer = document.querySelector('.mobile-drawer');
  var overlay = document.querySelector('.mobile-overlay');
  if (drawer && overlay) {
    drawer.classList.remove('open');
    overlay.classList.remove('open');
    document.body.style.overflow = '';
  }
}

function initMobileMenu() {
  // Attach click handler to mobile menu button
  var mobileBtn = document.querySelector('.navbar-mobile-btn');
  if (mobileBtn) {
    mobileBtn.addEventListener('click', toggleMobileMenu);
  }

  // Close menu when clicking overlay
  var overlay = document.querySelector('.mobile-overlay');
  if (overlay) {
    overlay.addEventListener('click', closeMobileMenu);
  }

  // Close menu when clicking close button in mobile drawer
  var closeBtn = document.querySelector('.mobile-drawer-close');
  if (closeBtn) {
    closeBtn.addEventListener('click', closeMobileMenu);
  }

  // Close menu when clicking a link in mobile drawer
  var drawer = document.querySelector('.mobile-drawer');
  if (drawer) {
    drawer.querySelectorAll('a').forEach(function(link) {
      link.addEventListener('click', closeMobileMenu);
    });
  }
}

// FAQ Accordion functionality
function toggleFAQ(button, container) {
  var answer = button.nextElementSibling;
  var icon = button.querySelector('.faq-icon');
  var isOpen = answer.style.maxHeight && answer.style.maxHeight !== '0px';
  var allowMultiple = container && container.dataset.allowMultiple === 'true';

  // If not allowing multiple, close all other items first
  if (!isOpen && !allowMultiple && container) {
    container.querySelectorAll('.faq-answer').forEach(function(a) {
      a.style.maxHeight = '0';
      var otherIcon = a.previousElementSibling.querySelector('.faq-icon');
      if (otherIcon) {
        otherIcon.textContent = '+';
        otherIcon.style.transform = 'rotate(0deg)';
      }
    });
  }

  if (isOpen) {
    answer.style.maxHeight = '0';
    if (icon) {
      icon.textContent = '+';
      icon.style.transform = 'rotate(0deg)';
    }
  } else {
    answer.style.maxHeight = answer.scrollHeight + 'px';
    if (icon) {
      icon.textContent = '−';
      icon.style.transform = 'rotate(180deg)';
    }
  }
}

// Before/After Slider functionality
function initBeforeAfterSliders() {
  document.querySelectorAll('.before-after-slider').forEach(function(container) {
    var beforeImage = container.querySelector('.before-image');
    var sliderLine = container.querySelector('.slider-line');
    var isDragging = false;

    function updateSlider(percentage) {
      var pos = Math.min(Math.max(percentage, 0), 100);
      if (beforeImage) {
        beforeImage.style.clipPath = 'inset(0 ' + (100 - pos) + '% 0 0)';
      }
      if (sliderLine) {
        sliderLine.style.left = pos + '%';
      }
    }

    function getPercentage(clientX) {
      var rect = container.getBoundingClientRect();
      var x = clientX - rect.left;
      return (x / rect.width) * 100;
    }

    container.addEventListener('mousedown', function(e) {
      isDragging = true;
      updateSlider(getPercentage(e.clientX));
      e.preventDefault();
    });

    document.addEventListener('mousemove', function(e) {
      if (!isDragging) return;
      updateSlider(getPercentage(e.clientX));
    });

    document.addEventListener('mouseup', function() {
      isDragging = false;
    });

    // Touch support
    container.addEventListener('touchstart', function(e) {
      isDragging = true;
      updateSlider(getPercentage(e.touches[0].clientX));
    }, { passive: true });

    container.addEventListener('touchmove', function(e) {
      if (!isDragging) return;
      updateSlider(getPercentage(e.touches[0].clientX));
    }, { passive: true });

    container.addEventListener('touchend', function() {
      isDragging = false;
    });
  });
}

// FAQ initialization - attach click handlers and open first item
function initFAQ() {
  document.querySelectorAll('.faq-container').forEach(function(container) {
    var buttons = container.querySelectorAll('.faq-toggle');

    buttons.forEach(function(button) {
      button.addEventListener('click', function() {
        toggleFAQ(this, container);
      });
    });

    // Open first item by default
    if (buttons.length > 0) {
      var firstButton = buttons[0];
      var firstAnswer = firstButton.nextElementSibling;
      var firstIcon = firstButton.querySelector('.faq-icon');
      if (firstAnswer) {
        firstAnswer.style.maxHeight = firstAnswer.scrollHeight + 'px';
        if (firstIcon) {
          firstIcon.textContent = '−';
          firstIcon.style.transform = 'rotate(180deg)';
        }
      }
    }
  });
}

// Gallery Lightbox functionality
function initGalleryLightbox() {
  document.querySelectorAll('.gallery-container').forEach(function(container) {
    var enableLightbox = container.getAttribute('data-lightbox') === 'true';
    if (!enableLightbox) return;

    var lightbox = container.querySelector('.gallery-lightbox');
    var lightboxImage = container.querySelector('.lightbox-image');
    var closeBtn = container.querySelector('.lightbox-close');
    var prevBtn = container.querySelector('.lightbox-prev');
    var nextBtn = container.querySelector('.lightbox-next');
    var items = container.querySelectorAll('.gallery-item');

    if (!lightbox || !lightboxImage || items.length === 0) return;

    var currentIndex = 0;

    function openLightbox(index) {
      currentIndex = index;
      var item = items[index];
      var src = item.getAttribute('data-src');
      var alt = item.getAttribute('data-alt') || '';
      lightboxImage.src = src;
      lightboxImage.alt = alt;
      lightbox.style.display = 'flex';
      document.body.style.overflow = 'hidden';
    }

    function closeLightbox() {
      lightbox.style.display = 'none';
      document.body.style.overflow = '';
    }

    function showNext() {
      currentIndex = (currentIndex + 1) % items.length;
      var item = items[currentIndex];
      lightboxImage.src = item.getAttribute('data-src');
      lightboxImage.alt = item.getAttribute('data-alt') || '';
    }

    function showPrev() {
      currentIndex = (currentIndex - 1 + items.length) % items.length;
      var item = items[currentIndex];
      lightboxImage.src = item.getAttribute('data-src');
      lightboxImage.alt = item.getAttribute('data-alt') || '';
    }

    // Click on gallery item to open lightbox
    items.forEach(function(item, i) {
      item.addEventListener('click', function() {
        openLightbox(i);
      });
    });

    // Close button
    if (closeBtn) {
      closeBtn.addEventListener('click', closeLightbox);
    }

    // Navigation buttons
    if (prevBtn) {
      prevBtn.addEventListener('click', function(e) {
        e.stopPropagation();
        showPrev();
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function(e) {
        e.stopPropagation();
        showNext();
      });
    }

    // Click on backdrop to close
    lightbox.addEventListener('click', function(e) {
      if (e.target === lightbox) {
        closeLightbox();
      }
    });

    // Keyboard navigation
    document.addEventListener('keydown', function(e) {
      if (lightbox.style.display !== 'flex') return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') showNext();
      if (e.key === 'ArrowLeft') showPrev();
    });
  });
}

// HeroSlider initialization with smooth transitions and auto-play
function initHeroSlider() {
  document.querySelectorAll('.hero-slider').forEach(function(slider) {
    var slides = slider.querySelectorAll('[data-slide]');
    var dots = slider.querySelectorAll('.slider-dot');
    var prevBtn = slider.querySelector('.slider-prev');
    var nextBtn = slider.querySelector('.slider-next');
    var currentIndex = 0;
    var totalSlides = slides.length;
    var autoPlayInterval = null;
    var autoPlayDelay = 5000; // 5 seconds

    if (totalSlides <= 1) return;

    // Setup slides for smooth transition (use opacity instead of display)
    // First slide stays relative to give container height, others are absolute
    slides.forEach(function(s, i) {
      if (i === 0) {
        s.style.position = 'relative';
        s.style.opacity = '1';
        s.style.zIndex = '10';
      } else {
        s.style.position = 'absolute';
        s.style.top = '0';
        s.style.left = '0';
        s.style.width = '100%';
        s.style.height = '100%';
        s.style.opacity = '0';
        s.style.zIndex = '0';
      }
      s.style.transition = 'opacity 0.5s ease-in-out';
      s.style.display = 'flex';
    });

    // Make slider container relative positioned
    slider.style.position = 'relative';
    slider.style.overflow = 'hidden';

    // Dot sizes - active is pill-shape (wider), inactive is circular
    var isMobile = window.innerWidth < 768;
    var dotActiveWidth = isMobile ? 24 : 32;
    var dotInactiveWidth = isMobile ? 8 : 12;

    function showSlide(index) {
      slides.forEach(function(s, i) {
        if (i === index) {
          s.style.opacity = '1';
          s.style.zIndex = '10';
        } else {
          s.style.opacity = '0';
          s.style.zIndex = '0';
        }
      });
      // Update dot styles - active gets pill-shape (wider width)
      dots.forEach(function(d, i) {
        if (i === index) {
          d.style.backgroundColor = '#ffffff';
          d.style.width = dotActiveWidth + 'px';
        } else {
          d.style.backgroundColor = 'rgba(255,255,255,0.5)';
          d.style.width = dotInactiveWidth + 'px';
        }
      });
      currentIndex = index;
    }

    function nextSlide() {
      showSlide((currentIndex + 1) % totalSlides);
    }

    function prevSlideFunc() {
      showSlide((currentIndex - 1 + totalSlides) % totalSlides);
    }

    // Auto-play
    function startAutoPlay() {
      stopAutoPlay();
      autoPlayInterval = setInterval(nextSlide, autoPlayDelay);
    }

    function stopAutoPlay() {
      if (autoPlayInterval) {
        clearInterval(autoPlayInterval);
        autoPlayInterval = null;
      }
    }

    // Pause on hover
    slider.addEventListener('mouseenter', stopAutoPlay);
    slider.addEventListener('mouseleave', startAutoPlay);

    if (prevBtn) {
      prevBtn.addEventListener('click', function() {
        prevSlideFunc();
        startAutoPlay(); // Reset timer after manual navigation
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', function() {
        nextSlide();
        startAutoPlay(); // Reset timer after manual navigation
      });
    }

    dots.forEach(function(dot, i) {
      dot.addEventListener('click', function() {
        showSlide(i);
        startAutoPlay(); // Reset timer after manual navigation
      });
    });

    // Start auto-play
    startAutoPlay();
  });
}

// Contact Form submission handling
function initContactForm() {
  document.querySelectorAll('.contact-form').forEach(function(container) {
    var form = container.querySelector('form.form-content');
    var successDiv = container.querySelector('.form-success');

    if (!form || !successDiv) return;

    form.addEventListener('submit', function(e) {
      // If form action is '#' or empty, prevent default and show success
      var action = form.getAttribute('action');
      if (!action || action === '#' || action === '') {
        e.preventDefault();
        form.style.display = 'none';
        successDiv.style.display = 'block';

        // Reset after 3 seconds
        setTimeout(function() {
          successDiv.style.display = 'none';
          form.style.display = 'block';
          form.reset();
        }, 3000);
      }
      // Otherwise let the form submit normally to the action URL
    });
  });
}

// Carousel (Doctors/Services) scroll functionality
function initCarousels() {
  document.querySelectorAll('.doctors-carousel, .services-carousel').forEach(function(carousel) {
    var scrollEl = carousel.querySelector('.carousel-scroll');
    var prevBtn = carousel.querySelector('.carousel-prev');
    var nextBtn = carousel.querySelector('.carousel-next');
    if (!scrollEl) return;

    function updateArrows() {
      var canScrollLeft = scrollEl.scrollLeft > 1;
      var canScrollRight = scrollEl.scrollLeft < scrollEl.scrollWidth - scrollEl.clientWidth - 1;
      var needsScroll = scrollEl.scrollWidth > scrollEl.clientWidth + 1;

      if (prevBtn) {
        prevBtn.style.display = needsScroll ? 'flex' : 'none';
        prevBtn.style.opacity = canScrollLeft ? '1' : '0.3';
        prevBtn.disabled = !canScrollLeft;
      }
      if (nextBtn) {
        nextBtn.style.display = needsScroll ? 'flex' : 'none';
        nextBtn.style.opacity = canScrollRight ? '1' : '0.3';
        nextBtn.disabled = !canScrollRight;
      }

      // Center cards when no scroll needed
      scrollEl.style.justifyContent = needsScroll ? 'flex-start' : 'center';
      scrollEl.style.padding = needsScroll ? '16px 48px' : '16px 0';
    }

    var scrollAmount = function() { return scrollEl.clientWidth; };
    if (prevBtn) {
      prevBtn.addEventListener('click', function() {
        scrollEl.scrollBy({ left: -scrollAmount(), behavior: 'smooth' });
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function() {
        scrollEl.scrollBy({ left: scrollAmount(), behavior: 'smooth' });
      });
    }

    scrollEl.addEventListener('scroll', updateArrows);
    window.addEventListener('resize', updateArrows);
    updateArrows();
  });
}

// Mobile padding & order overrides
function applyMobileOverrides() {
  var isMobile = window.innerWidth < 768;
  document.querySelectorAll('[data-mobile-padding-x]').forEach(function(el) {
    if (isMobile) {
      var px = el.getAttribute('data-mobile-padding-x') + 'px';
      el.style.paddingLeft = px;
      el.style.paddingRight = px;
    }
  });
  document.querySelectorAll('[data-mobile-padding-y]').forEach(function(el) {
    if (isMobile) {
      var py = el.getAttribute('data-mobile-padding-y') + 'px';
      el.style.paddingTop = py;
      el.style.paddingBottom = py;
    }
  });
  document.querySelectorAll('[data-mobile-order]').forEach(function(el) {
    if (isMobile) {
      el.style.order = el.getAttribute('data-mobile-order');
    }
  });
  document.querySelectorAll('[data-mobile-align-items]').forEach(function(el) {
    if (isMobile) {
      var val = el.getAttribute('data-mobile-align-items');
      var map = { start: 'flex-start', center: 'center', end: 'flex-end', stretch: 'stretch' };
      el.style.alignItems = map[val] || val;
    }
  });
  document.querySelectorAll('[data-mobile-bg-position]').forEach(function(el) {
    if (isMobile) {
      el.style.backgroundPosition = el.getAttribute('data-mobile-bg-position');
    }
  });
}

// Active nav link tracking via IntersectionObserver
function initNavActiveTracking() {
  var navLinks = document.querySelectorAll('.nav-link');
  var anchorLinks = [];
  navLinks.forEach(function(link) {
    var href = link.getAttribute('href');
    if (href && href.startsWith('#')) anchorLinks.push(href.slice(1));
  });
  if (anchorLinks.length === 0) return;

  var sections = [];
  anchorLinks.forEach(function(id) {
    var el = document.getElementById(id);
    if (el) sections.push(el);
  });
  if (sections.length === 0) return;

  // Add ::after pseudo-element via CSS class
  var style = document.createElement('style');
  style.textContent = '.nav-link{position:relative;padding:8px 0}.nav-link::after{content:"";position:absolute;bottom:0;left:0;right:0;height:3px;background:linear-gradient(90deg,#fda0b8,#f9c26f);border-radius:2px;transform:scaleX(0);transform-origin:center;transition:transform .3s cubic-bezier(.4,0,.2,1)}.nav-link.active::after{transform:scaleX(1)}';
  document.head.appendChild(style);

  function setActive(activeId) {
    navLinks.forEach(function(link) {
      var href = link.getAttribute('href');
      if (href === '#' + activeId) {
        link.style.fontWeight = '700';
        link.classList.add('active');
      } else if (href && href.startsWith('#')) {
        link.style.fontWeight = '500';
        link.classList.remove('active');
      }
    });
  }

  // Click handler for nav links
  navLinks.forEach(function(link) {
    link.addEventListener('click', function(e) {
      var href = link.getAttribute('href');
      if (href && href.startsWith('#')) {
        e.preventDefault();
        var targetId = href.slice(1);
        setActive(targetId);
        var target = document.getElementById(targetId);
        if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  });

  // IntersectionObserver for scroll tracking
  var observer = new IntersectionObserver(function(entries) {
    entries.forEach(function(entry) {
      if (entry.isIntersecting) {
        setActive(entry.target.id);
      }
    });
  }, { threshold: 0.2, rootMargin: '-80px 0px 0px 0px' });

  sections.forEach(function(el) { observer.observe(el); });
}

// Language Selector Dropdown
function initLangSelector() {
  var container = document.querySelector('.navbar-lang-container');
  if (!container) return;
  var btn = container.querySelector('.navbar-lang-btn');
  var dropdown = container.querySelector('.navbar-lang-dropdown');
  if (!btn || !dropdown) return;

  btn.addEventListener('click', function() {
    var isOpen = dropdown.style.display === 'block';
    dropdown.style.display = isOpen ? 'none' : 'block';
  });

  var options = container.querySelectorAll('.navbar-lang-option');
  options.forEach(function(option) {
    option.addEventListener('click', function() {
      var lang = option.getAttribute('data-lang');
      var flag = lang === 'TH' ? '\u{1F1F9}\u{1F1ED}' : '\u{1F1FA}\u{1F1F8}';
      btn.textContent = flag + ' ' + lang + ' \u25BE';
      dropdown.style.display = 'none';
      // Highlight selected
      options.forEach(function(o) { o.style.backgroundColor = 'transparent'; });
      option.style.backgroundColor = '#f3f4f6';
      // Switch multilingual text content + font
      var langKey = lang.toLowerCase();
      var thaiRegex = /[\u0E00-\u0E7F]/;
      document.querySelectorAll('.i18n-text').forEach(function(el) {
        var text = el.getAttribute('data-i18n-' + langKey);
        if (text !== null && text !== '') {
          el.textContent = text;
        } else {
          var thText = el.getAttribute('data-i18n-th');
          if (thText) { el.textContent = thText; text = thText; }
        }
        // Switch font: Thai text uses --font-family, non-Thai uses --font-family-en
        var displayText = text || el.textContent || '';
        el.style.fontFamily = thaiRegex.test(displayText) ? '' : 'var(--font-family-en, var(--font-family))';
      });
    });
  });

  // Close on outside click
  document.addEventListener('mousedown', function(e) {
    if (!container.contains(e.target)) {
      dropdown.style.display = 'none';
    }
  });
}

// Filterable Features — tab switching, filtering, pagination
function initFilterableFeatures() {
  document.querySelectorAll('.filterable-features').forEach(function(container) {
    var tabs = container.querySelectorAll('.feature-tab');
    var cards = container.querySelectorAll('.feature-card');
    var grid = container.querySelector('.feature-grid');
    var prevBtn = container.querySelector('.feature-prev');
    var nextBtn = container.querySelector('.feature-next');
    var cardsPerRow = parseInt(container.dataset.cardsPerRow || '3');
    var rowsPerPage = 2;
    var itemsPerPage = cardsPerRow * rowsPerPage;
    var activeCategory = 'All';
    var currentPage = 0;

    function getFiltered() {
      var arr = [];
      cards.forEach(function(card) {
        if (activeCategory === 'All' || card.dataset.category === activeCategory) arr.push(card);
      });
      return arr;
    }

    function render() {
      var filtered = getFiltered();
      var totalPages = Math.ceil(filtered.length / itemsPerPage);
      if (currentPage >= totalPages) currentPage = Math.max(0, totalPages - 1);
      var start = currentPage * itemsPerPage;
      var end = start + itemsPerPage;

      cards.forEach(function(card) { card.style.display = 'none'; });
      filtered.forEach(function(card, i) {
        card.style.display = (i >= start && i < end) ? '' : 'none';
      });

      // Update arrows
      if (prevBtn) {
        prevBtn.style.display = filtered.length > itemsPerPage ? 'flex' : 'none';
        prevBtn.style.opacity = currentPage <= 0 ? '0.3' : '1';
        prevBtn.style.cursor = currentPage <= 0 ? 'default' : 'pointer';
      }
      if (nextBtn) {
        nextBtn.style.display = filtered.length > itemsPerPage ? 'flex' : 'none';
        nextBtn.style.opacity = currentPage >= totalPages - 1 ? '0.3' : '1';
        nextBtn.style.cursor = currentPage >= totalPages - 1 ? 'default' : 'pointer';
      }
    }

    tabs.forEach(function(tab) {
      tab.addEventListener('click', function() {
        activeCategory = tab.dataset.category || 'All';
        currentPage = 0;
        // Update tab styles
        tabs.forEach(function(t) {
          var isActive = t.dataset.category === activeCategory;
          var catColor = t.dataset.color || '#E8834A';
          t.style.backgroundColor = isActive ? catColor : '#ffffff';
          t.style.color = isActive ? '#ffffff' : (container.style.color || '#2C2C2B');
          t.style.border = isActive ? 'none' : '1px solid #d1d5db';
          t.style.fontWeight = isActive ? '600' : '500';
          t.classList.toggle('active', isActive);
        });
        render();
      });
    });

    if (prevBtn) prevBtn.addEventListener('click', function() {
      if (currentPage > 0) { currentPage--; render(); }
    });
    if (nextBtn) nextBtn.addEventListener('click', function() {
      var filtered = getFiltered();
      var totalPages = Math.ceil(filtered.length / itemsPerPage);
      if (currentPage < totalPages - 1) { currentPage++; render(); }
    });

    render();
  });
}

// Initialize on page load
document.addEventListener('DOMContentLoaded', function() {
  initMobileMenu();
  initBeforeAfterSliders();
  initFAQ();
  initHeroSlider();
  initGalleryLightbox();
  initContactForm();
  initCarousels();
  initFilterableFeatures();
  applyMobileOverrides();
  initNavActiveTracking();
  initLangSelector();
});
window.addEventListener('resize', applyMobileOverrides);
`;
};

/**
 * Generate complete HTML page with extracted CSS
 */
export const generateStaticPage = (
  content: string,
  theme: ThemeSettings,
  pageTitle: string,
  siteName: string,
  seo?: { title?: string; description?: string; keywords?: string },
  options?: { favicon?: string; lineLiffId?: string; officialAccountId?: number; facebookUrl?: string; gaMeasurementId?: string }
): string => {
  const { html, css } = renderToStaticHtml(content, theme);
  const interactiveJS = getInteractiveJS();
  const lineLiffId = options?.lineLiffId;
  const facebookUrl = options?.facebookUrl;

  // Google Analytics 4 (gtag.js) — injected into <head> only when a valid
  // Measurement ID (G-XXXXXXXXXX) is configured for the site.
  const gaMeasurementId =
    options?.gaMeasurementId && /^G-[A-Z0-9]+$/i.test(options.gaMeasurementId.trim())
      ? options.gaMeasurementId.trim()
      : "";
  const gtagHtml = gaMeasurementId ? `
  <!-- Google tag (gtag.js) -->
  <script async src="https://www.googletagmanager.com/gtag/js?id=${gaMeasurementId}"></script>
  <script>
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    gtag('js', new Date());
    gtag('config', '${gaMeasurementId}');
  </script>` : "";
  const lineChatBubbleHtml = lineLiffId ? `
  <style>
    @keyframes chatPulse {
      0%   { box-shadow: 0 4px 16px rgba(6,199,85,0.45), 0 0 0 0 rgba(6,199,85,0.45); }
      70%  { box-shadow: 0 4px 16px rgba(6,199,85,0.45), 0 0 0 14px rgba(6,199,85,0); }
      100% { box-shadow: 0 4px 16px rgba(6,199,85,0.45), 0 0 0 0 rgba(6,199,85,0); }
    }
    #line-chat-bubble { animation: chatPulse 2.5s ease-out infinite; }
    #line-chat-bubble:hover { transform: scale(1.08) !important; }
  </style>
  <a href="https://line.ishealth.app/mehealth.ishealth.app" target="_blank" rel="noopener noreferrer"
     id="line-chat-bubble"
     aria-label="Chat with us"
     style="
       position: fixed;
       bottom: 24px;
       right: 24px;
       width: 48px;
       height: 48px;
       border-radius: 50%;
       display: inline-flex;
       align-items: center;
       justify-content: center;
       z-index: 9999;
       text-decoration: none;
       transition: transform 0.2s;
     "
  >
    <img src="https://pro.ishealth.app/static/images/landing/line-circular-icon.png"
         alt="LINE"
         width="48" height="48"
         style="width:48px;height:48px;border-radius:50%;display:block;" />
  </a>` : "";

  // Facebook floating button — stacked above the LINE bubble when present.
  // FB icon (54px) is slightly larger than LINE (48px) because the PNG has more
  // built-in padding; centered on the same right edge so both look the same width.
  const fbBottom = lineLiffId ? 92 : 24; // 24 + 48 (line) + 20 gap
  const facebookBubbleHtml = facebookUrl ? `
  <style>
    @keyframes fbPulse {
      0%   { box-shadow: 0 4px 16px rgba(24,119,242,0.45), 0 0 0 0 rgba(24,119,242,0.45); }
      70%  { box-shadow: 0 4px 16px rgba(24,119,242,0.45), 0 0 0 14px rgba(24,119,242,0); }
      100% { box-shadow: 0 4px 16px rgba(24,119,242,0.45), 0 0 0 0 rgba(24,119,242,0); }
    }
    #facebook-bubble { animation: fbPulse 2.5s ease-out infinite; }
    #facebook-bubble:hover { transform: scale(1.08) !important; }
  </style>
  <a href="${facebookUrl}" target="_blank" rel="noopener noreferrer"
     id="facebook-bubble"
     aria-label="Facebook"
     style="
       position: fixed;
       bottom: ${fbBottom}px;
       right: 23px;
       width: 50px;
       height: 50px;
       border-radius: 50%;
       display: inline-flex;
       align-items: center;
       justify-content: center;
       z-index: 9999;
       text-decoration: none;
       transition: transform 0.2s;
     "
  >
    <img src="https://pro.ishealth.app/static/images/landing/facebook-circular-icon.png"
         alt="Facebook"
         width="50" height="50"
         style="width:50px;height:50px;border-radius:50%;display:block;" />
  </a>` : "";

  const title = seo?.title || pageTitle;
  const description = seo?.description || `${pageTitle} - ${siteName}`;

  return `<!DOCTYPE html>
<html lang="th">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">${gtagHtml}
  <title>${title} | ${siteName}</title>
  <meta name="description" content="${description}">
  ${seo?.keywords ? `<meta name="keywords" content="${seo.keywords}">` : ""}
  ${options?.favicon ? `<link rel="icon" href="${options.favicon}" type="image/x-icon">` : ""}
  <link rel="preconnect" href="${EXTERNAL_URLS.googleFonts.preconnect}">
  <link rel="preconnect" href="${EXTERNAL_URLS.googleFonts.gstatic}" crossorigin>
  <link href="${getGoogleFontsUrl(theme.font)}" rel="stylesheet">
  ${theme.fontEN && theme.fontEN !== theme.font ? `<link href="${getGoogleFontsUrl(theme.fontEN)}" rel="stylesheet">` : ""}
  ${(() => {
    // Collect all unique fontFamily values from content nodes
    try {
      const parsed = JSON.parse(content);
      const fonts = new Set<string>();
      for (const node of Object.values(parsed) as any[]) {
        const ff = node?.props?.fontFamily;
        if (typeof ff === "string" && ff && ff !== theme.font) fonts.add(ff);
      }
      return Array.from(fonts).map((f: string) => `<link href="${getGoogleFontsUrl(f)}" rel="stylesheet">`).join("\n  ");
    } catch { return ""; }
  })()}
  <style>
    :root {
      --color-primary: ${theme.primary};
      --color-secondary: ${theme.secondary};
      --font-family: '${theme.font}', sans-serif;
      --font-family-en: '${theme.fontEN || theme.font}', sans-serif;
    }
    * {
      box-sizing: border-box;
    }
    html, body {
      margin: 0;
      padding: 0;
      width: 100%;
      min-height: 100%;
      overflow-x: hidden;
    }
    body {
      font-family: var(--font-family);
      line-height: 1.6;
      color: #1f2937;
      background-color: #ffffff;
    }
    .export-root h1, .export-root h2, .export-root h3, .export-root h4, .export-root h5, .export-root h6,
    .export-root p, .export-root span, .export-root a, .export-root li,
    .export-root td, .export-root th, .export-root label,
    .export-root button, .export-root input, .export-root textarea, .export-root select {
      font-family: inherit;
    }
    .export-root {
      width: 100%;
      min-height: 100vh;
      overflow-x: hidden;
    }
    img {
      max-width: 100%;
    }
    a {
      text-decoration: none;
      transition: opacity 0.2s;
    }
    a:hover {
      opacity: 0.85;
    }
    /* Mobile Menu Styles */
    .mobile-overlay {
      display: none;
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.5);
      z-index: 998;
    }
    .mobile-overlay.open {
      display: block;
    }
    .mobile-drawer {
      position: fixed;
      top: 0;
      right: 0;
      bottom: 0;
      width: 80vw;
      max-width: 300px;
      background: white;
      z-index: 999;
      transform: translateX(100%);
      transition: transform 0.3s ease;
      overflow-y: auto;
      display: flex;
      flex-direction: column;
      box-shadow: -4px 0 20px rgba(0,0,0,0.15);
    }
    .mobile-drawer.open {
      transform: translateX(0);
    }

    /* Responsive show/hide */
    [data-hide-on-desktop="true"] {
      display: none !important;
    }

    /* Flex row container - children distribute evenly (skip fixed-width children) */
    .container-row > *:not(.container-fixed-width) {
      flex: 1 1 0 !important;
      min-width: 0 !important;
      max-width: 100% !important;
    }

    /* Card components in flex row - proper sizing */
    .container-row .before-after-slider,
    .container-row .testimonial-card {
      flex: 1 1 0 !important;
      min-width: 200px;
      max-width: 100%;
    }

    /* Doctor card - size controlled by parent --card-width CSS variable */
    .doctor-card {
      flex: 0 0 var(--card-width, 280px);
      min-width: 0;
    }

    /* Service card - size controlled by parent --card-width CSS variable */
    .service-card {
      flex: 0 0 var(--card-width, 280px);
      min-width: 0;
    }

    /* ===== HOVER & INTERACTION EFFECTS ===== */

    /* Buttons */
    button, a[style*="background"] {
      transition: all 0.2s ease;
    }
    .hero-slider a[style*="background"]:hover,
    .hero-section a[style*="background"]:hover,
    .promotion-banner a[style*="background"]:hover {
      transform: scale(1.05);
      box-shadow: 0 10px 20px rgba(0,0,0,0.15);
    }
    .contact-form button:hover {
      opacity: 0.9;
      transform: translateY(-1px);
    }

    /* Cards - lift effect on hover */
    .service-card,
    .doctor-card,
    .testimonial-card {
      transition: transform 0.2s ease, box-shadow 0.2s ease;
    }
    .service-card:hover,
    .doctor-card:hover {
      transform: translateY(-4px);
      box-shadow: 0 12px 24px rgba(0,0,0,0.12) !important;
    }
    .testimonial-card:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 16px rgba(0,0,0,0.1) !important;
    }

    /* Service/Doctor card image zoom */
    .service-card img,
    .doctor-card img {
      transition: transform 0.3s ease;
    }
    .service-card:hover img,
    .doctor-card:hover img {
      transform: scale(1.05);
    }

    /* Navigation links */
    .navbar-desktop a {
      transition: color 0.2s ease, opacity 0.2s ease;
      position: relative;
    }
    .navbar-desktop a:not([style*="background"]):hover {
      opacity: 0.7;
    }
    .navbar-desktop a[style*="background"]:hover {
      opacity: 0.9;
      transform: scale(1.02);
    }

    /* Mobile drawer links */
    .mobile-drawer a {
      transition: background-color 0.2s ease;
    }
    .mobile-drawer a:hover {
      background-color: rgba(0,0,0,0.05);
    }

    /* Slider arrows */
    .slider-prev,
    .slider-next {
      padding: 0 !important;
      margin: 0 !important;
      aspect-ratio: 1 / 1;
      box-sizing: border-box;
    }
    .slider-prev:hover,
    .slider-next:hover {
      background-color: rgba(255,255,255,0.4) !important;
    }

    /* Slider dots */
    .slider-dot {
      transition: all 0.2s ease;
    }
    .slider-dot:hover {
      transform: scale(1.2);
    }

    /* Gallery items */
    .gallery-item {
      transition: transform 0.3s ease;
      cursor: pointer;
    }
    .gallery-item:hover {
      transform: scale(1.02);
    }
    .gallery-item img {
      transition: transform 0.3s ease;
    }
    .gallery-item:hover img {
      transform: scale(1.1);
    }

    /* FAQ toggle */
    .faq-toggle {
      transition: background-color 0.2s ease;
    }
    .faq-toggle:hover {
      background-color: rgba(0,0,0,0.03);
    }

    /* Social links */
    .floating-cta a {
      transition: transform 0.2s ease, box-shadow 0.2s ease;
    }
    .floating-cta a:hover {
      transform: scale(1.1);
      box-shadow: 0 6px 16px rgba(0,0,0,0.2);
    }

    /* Footer links */
    footer a {
      transition: opacity 0.2s ease;
    }
    footer a:hover {
      opacity: 0.7 !important;
    }

    /* Before/After slider cursor */
    .before-after-slider {
      cursor: ew-resize;
    }
    .slider-line:hover .slider-handle {
      transform: translate(-50%, -50%) scale(1.1);
      box-shadow: 0 6px 12px rgba(0,0,0,0.4);
    }

    /* Input focus states */
    .contact-form input:focus,
    .contact-form textarea:focus,
    .contact-form select:focus {
      border-color: var(--color-primary, #3b82f6) !important;
      outline: none;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }

    /* ===== RESPONSIVE STYLES (Mobile < 768px) ===== */
    @media (max-width: 767px) {
      /* ----- NAVBAR ----- */
      .navbar-desktop {
        display: none !important;
      }
      .navbar-mobile-btn {
        display: flex !important;
      }
      .navbar {
        padding: 12px 16px !important;
      }
      .navbar img {
        height: 32px !important;
      }

      /* ----- HERO SECTION ----- */
      .hero-section {
        min-height: 300px !important;
      }
      .hero-section h1 {
        font-size: 2rem !important;
      }
      .hero-section p {
        font-size: 1rem !important;
        margin-bottom: 24px !important;
      }
      .hero-section > div > div {
        padding: 40px 16px !important;
      }
      .hero-section a[style*="padding"] {
        padding: 8px 20px !important;
        font-size: 0.875rem !important;
      }
      /* Hero buttons: wrap on mobile */
      .hero-section div[style*="display: flex"][style*="gap"] {
        flex-wrap: wrap !important;
        justify-content: center !important;
      }

      /* ----- HERO SLIDER ----- */
      .hero-slider {
        width: 100% !important;
        min-height: 280px !important;
      }
      .hero-slider [data-slide] {
        min-height: 280px !important;
      }
      .hero-slider h1 {
        font-size: 2rem !important;
      }
      .hero-slider p {
        font-size: 1rem !important;
      }
      .hero-slider a[style*="padding"] {
        padding: 8px 20px !important;
        font-size: 0.875rem !important;
      }
      .slider-prev, .slider-next {
        width: 36px !important;
        height: 36px !important;
        min-width: 36px !important;
        min-height: 36px !important;
        padding: 0 !important;
      }
      .slider-prev {
        left: 8px !important;
      }
      .slider-next {
        right: 8px !important;
      }
      .slider-prev svg, .slider-next svg {
        width: 18px !important;
        height: 18px !important;
      }
      /* Dots - height only, width controlled by JS for active/inactive state */
      .slider-dot {
        height: 8px !important;
      }

      /* ----- TEXT ROW WRAPPING ----- */
      div[style*="align-items: baseline"] {
        flex-wrap: wrap !important;
      }
      div[style*="align-items: baseline"] > span {
        white-space: nowrap !important;
      }

      /* ----- RESPONSIVE SHOW/HIDE ----- */
      [data-hide-on-desktop="true"] {
        display: flex !important;
      }
      [data-hide-on-mobile="true"] {
        display: none !important;
      }

      /* ----- FLEX CONTAINERS ----- */
      .container-row.flex-container-stack {
        flex-direction: column !important;
      }
      .container-row.flex-container-stack > * {
        flex: none !important;
        width: 100% !important;
      }
      .container-row.flex-container-scroll {
        overflow-x: auto;
        flex-wrap: nowrap !important;
        -webkit-overflow-scrolling: touch;
      }
      .container-row.flex-container-wrap {
        flex-wrap: wrap !important;
      }

      /* ----- FOOTER ----- */
      footer {
        padding: 32px 16px !important;
      }
      footer > div > div {
        grid-template-columns: 1fr !important;
        gap: 24px !important;
      }
      footer h3 {
        font-size: 1.125rem !important;
      }
      footer h4 {
        font-size: 0.875rem !important;
      }

      /* ----- TESTIMONIAL ----- */
      .testimonial-card {
        padding: 16px !important;
      }
      .testimonial-card blockquote,
      .testimonial-card [style*="font-size: 1.125rem"] {
        font-size: 0.95rem !important;
      }

      /* ----- SERVICE/DOCTOR CAROUSEL: 1 card on mobile ----- */
      .services-carousel .service-card,
      .doctors-carousel .doctor-card {
        flex: 0 0 80% !important;
        min-width: 80% !important;
      }
      /* Smaller arrow buttons on mobile */
      .services-carousel button,
      .doctors-carousel button {
        width: 32px !important;
        height: 32px !important;
      }
      .services-carousel button svg,
      .doctors-carousel button svg {
        width: 18px !important;
        height: 18px !important;
      }

      /* ----- SERVICE CARD ----- */
      .service-card img {
        height: 140px !important;
      }
      .service-card > div:last-child {
        padding: 16px !important;
      }
      .service-card h3 {
        font-size: 1rem !important;
      }
      .service-card p {
        font-size: 0.8125rem !important;
      }

      /* ----- DOCTOR CARD ----- */
      .doctor-card img {
        width: 80px !important;
        height: 80px !important;
      }
      .doctor-card h3 {
        font-size: 1rem !important;
      }

      /* ----- GALLERY ----- */
      .gallery-grid {
        grid-template-columns: repeat(2, 1fr) !important;
        gap: 8px !important;
      }

      /* ----- FAQ ----- */
      .faq-toggle {
        font-size: 0.9375rem !important;
        padding: 16px !important;
      }
      .faq-answer p {
        font-size: 0.8125rem !important;
        padding: 0 16px 16px !important;
      }

      /* ----- PROMOTION BANNER ----- */
      .promotion-banner {
        padding: 24px 16px !important;
      }
      .promotion-banner h2 {
        font-size: 1.25rem !important;
      }
      .promotion-banner [style*="font-size: 3rem"],
      .promotion-banner [style*="font-size: 2.5rem"] {
        font-size: 2.25rem !important;
      }

      /* ----- CONTACT INFO ----- */
      .contact-info {
        padding: 24px 16px !important;
      }
      .contact-info h3 {
        font-size: 1.25rem !important;
      }
      .contact-info > div > div {
        grid-template-columns: 1fr !important;
      }

      /* ----- CONTACT FORM ----- */
      .contact-form {
        padding: 16px !important;
      }
      .contact-form input,
      .contact-form textarea,
      .contact-form select {
        padding: 10px 12px !important;
        font-size: 0.875rem !important;
      }
      .contact-form button {
        padding: 10px 16px !important;
        font-size: 0.875rem !important;
      }

      /* ----- FILTERABLE FEATURES ----- */
      .filterable-features .feature-grid {
        grid-template-columns: 1fr !important;
      }
      .filterable-features .feature-card {
        padding: 16px !important;
        min-height: 190px !important;
      }

      /* ----- BEFORE/AFTER ----- */
      .before-after-slider {
        min-height: 240px !important;
      }

      /* ----- FLOATING CTA ----- */
      .floating-cta {
        bottom: 16px !important;
        right: 16px !important;
        padding: 12px 16px !important;
      }
      .floating-cta span:not(:first-child) {
        display: none !important;
      }

      /* ----- BUTTONS ----- */
      .btn-sm {
        padding: 6px 12px !important;
        font-size: 13px !important;
      }
      .btn-md {
        padding: 10px 20px !important;
        font-size: 14px !important;
      }
      .btn-lg {
        padding: 12px 24px !important;
        font-size: 16px !important;
      }

      /* ----- GENERAL ----- */
      [style*="padding: 48px"],
      [style*="padding: 32px 48px"] {
        padding: 24px 16px !important;
      }
    }

    /* ===== TABLET STYLES (768px - 1023px) ===== */
    @media (min-width: 768px) and (max-width: 1023px) {
      .gallery-grid {
        grid-template-columns: repeat(2, 1fr) !important;
      }
      footer > div > div {
        grid-template-columns: repeat(2, 1fr) !important;
      }
      .container-row.flex-container-stack > * {
        flex: 1 1 45% !important;
      }
    }

    /* Extracted MUI/Emotion CSS */
    ${css}
  </style>
</head>
<body>
  ${html}
  ${lineChatBubbleHtml}
  ${facebookBubbleHtml}
  <script>
${interactiveJS}
  </script>
</body>
</html>`;
};

export default generateStaticPage;
